/**
 * useMentionableUsers
 *
 * Returns the set of users that can be @-mentioned in a comment thread
 * for the given evaluation.  "Mentionable" = anyone who shares at least one
 * channel with the current user (coach members + athlete members), which
 * matches the backend access model for who can view a comment.
 *
 * Strategy:
 *  1. Fetch the current user's channel memberships (already cached by
 *     useChannelMemberships — same query key reuse).
 *  2. Optionally restrict to `channelSlugs` when provided (e.g. the
 *     channel this template belongs to), otherwise fall back to all slugs.
 *  3. Fan out getChannelMembers per slug and deduplicate by user_id,
 *     excluding the current user (mentioning yourself is pointless).
 *
 * The result is used solely for autocomplete — the backend independently
 * resolves @tokens and validates access.
 */

import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { getMyChannelMemberships } from '@/services/channelApi';
import { getChannelMembers } from '@/services/channelApi';
import type { MentionableUser } from '@/services/commentApi';

interface UseMentionableUsersOptions {
  /** Restrict to these slugs.  When omitted, all user channels are used. */
  channelSlugs?: string[];
}

export function useMentionableUsers(options: UseMentionableUsersOptions = {}) {
  const { user } = useAuth();

  return useQuery<MentionableUser[]>({
    queryKey: ['mentionable-users', user?.id, options.channelSlugs?.slice().sort()],
    enabled: !!user?.id,
    staleTime: 60_000,
    queryFn: async (): Promise<MentionableUser[]> => {
      // Determine which channel slugs to look up.
      let slugs: string[];
      if (options.channelSlugs && options.channelSlugs.length > 0) {
        slugs = options.channelSlugs;
      } else {
        // Fall back: all channels the user belongs to.
        const memberships = await getMyChannelMemberships();
        slugs = memberships.map((m) => m.channel_slug);
      }

      if (slugs.length === 0) return [];

      const results = await Promise.all(slugs.map((slug) => getChannelMembers(slug)));

      const seen = new Set<string>();
      const mentionable: MentionableUser[] = [];
      for (const members of results) {
        for (const member of members) {
          // Exclude the current user — mentioning yourself is meaningless.
          if (member.user_id === user!.id) continue;
          if (seen.has(member.user_id)) continue;
          seen.add(member.user_id);
          const displayName = member.display_name ?? member.email;
          mentionable.push({
            user_id: member.user_id,
            display_name: displayName,
            role: member.member_role ?? member.role,
          });
        }
      }

      // Sort: coaches/owners first, then alphabetically within each tier.
      const rolePriority = (role: string | undefined): number => {
        if (role === 'owner') return 0;
        if (role === 'coach' || role === 'channel_admin') return 1;
        if (role === 'channel_coach') return 2;
        return 3; // athlete / viewer / unknown
      };

      mentionable.sort((a, b) => {
        const rDiff = rolePriority(a.role) - rolePriority(b.role);
        if (rDiff !== 0) return rDiff;
        return a.display_name.localeCompare(b.display_name);
      });

      return mentionable;
    },
  });
}
